import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { VoiceProvider } from './context/VoiceContext';
import { DashboardLayout } from './components/layout/DashboardLayout';
import PublicLayout from './components/layout/PublicLayout';
import GlobalWakeWordListener from './components/GlobalWakeWordListener';
import CookieConsentBanner from './components/CookieConsentBanner';
import GlobalVoiceAssistant from './components/GlobalVoiceAssistant';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import ChatPage from './pages/ChatPage';
import AiraEchoMainPage from './pages/AiraEchoMainPage';
import VoiceAgentPage from './pages/VoiceAgentPage';
import AssistantPage from './pages/AssistantPage';
import TravelPage from './pages/TravelPage';
import StaysPage from './pages/StaysPage';
import InvestmentGuruPage from './pages/InvestmentGuruPage';
import CreativeStudioPage from './pages/CreativeStudioPage';
import LifeManagerPage from './pages/LifeManagerPage';
import LifeCoachPage from './pages/LifeCoachPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import ViralCardsPage from './pages/ViralCardsPage';
import BusinessBuilderPage from './pages/BusinessBuilderPage';
import LearningHubPage from './pages/LearningHubPage';
import OpportunityRadarPage from './pages/OpportunityRadarPage';
import FuturePreviewPage from './pages/FuturePreviewPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/SubscriptionCancelPage';
import AdminPage from './pages/AdminPage';
import GalleryPage from './pages/GalleryPage';
import VideoCallPage from './pages/VideoCallPage';
import CreatorModePage from './pages/CreatorModePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AiraEchoPage from './pages/echo/AiraEchoPageNew';
import FutureEchoPage from './pages/FutureEchoPage';
import EchoSoulsPage from './pages/EchoSoulsPage';
import ExperiencesPage from './pages/ExperiencesPage';
import TripPlannerPage from './pages/TripPlannerPage';
import FilmStudioPage from './pages/FilmStudioPage';
import AiraSearchPage from './pages/search/AiraSearchPage';
import TrainBookingPage from './pages/trains/TrainBookingPage';
import BookingPage from './pages/BookingPage';
import AiraTestPage from './pages/AiraTestPage';
import VoiceTestPage from './pages/VoiceTestPage';
import SafeBookingPage from './pages/SafeBookingPage';
import CompleteBookingPage from './pages/CompleteBookingPage';
import ToursPage from './pages/ToursPage';
import CabsPage from './pages/CabsPage';
import InsurancePage from './pages/InsurancePage';

import './App.css';

// Protected Route Component - Only for features that MUST require login
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Skip if user data passed from AuthCallback
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the attempted URL for redirect after login
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  return children;
};

// Booking Protected Route - Requires login but preserves booking data
const BookingProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to auth but preserve booking state
    return <Navigate to="/auth" state={{ from: location.pathname, bookingData: location.state }} replace />;
  }

  return children;
};

// Public Route - redirect to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030014] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Check if user was trying to access a specific page
    const redirectTo = location.state?.from || '/dashboard';
    return <Navigate to={redirectTo} state={location.state?.bookingData ? { ...location.state.bookingData } : undefined} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback) - synchronous check
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* ===== FULLY PUBLIC ROUTES (No Login Required) ===== */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/login" element={<Navigate to="/auth" replace />} />
      <Route path="/signup" element={<Navigate to="/auth" replace />} />
      <Route path="/pricing" element={<SubscriptionPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      
      {/* Internal Testing Route */}
      <Route path="/aira-test" element={<AiraTestPage />} />
      <Route path="/voice-test" element={<VoiceTestPage />} />

      {/* ===== PUBLIC TRAVEL ROUTES (Search without Login) ===== */}
      <Route element={<PublicLayout />}>
        <Route path="/travel" element={<TravelPage />} />
        <Route path="/stays" element={<StaysPage />} />
        <Route path="/search" element={<AiraSearchPage />} />
        <Route path="/trains" element={<TrainBookingPage />} />
        <Route path="/tours" element={<ToursPage />} />
        <Route path="/cabs" element={<CabsPage />} />
        <Route path="/insurance" element={<InsurancePage />} />
      </Route>

      {/* ===== BOOKING ROUTES (Identification at Confirmation - NOT Login Wall) ===== */}
      <Route path="/booking/:bookingId" element={<BookingPage />} />
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/safe-booking" element={<SafeBookingPage />} />
      <Route path="/complete-booking" element={<CompleteBookingPage />} />

      {/* ===== PUBLIC DASHBOARD ROUTES (No Login Required for Browsing) ===== */}
      <Route element={<DashboardLayout />}>
        <Route path="/dashboard" element={<AiraEchoMainPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/assistant" element={<AssistantPage />} />
        <Route path="/voice" element={<VoiceAgentPage />} />
        <Route path="/investment" element={<InvestmentGuruPage />} />
        <Route path="/creative" element={<CreativeStudioPage />} />
        <Route path="/creator-mode" element={<CreatorModePage />} />
        <Route path="/life-manager" element={<LifeManagerPage />} />
        <Route path="/life-coach" element={<LifeCoachPage />} />
        <Route path="/viral" element={<ViralCardsPage />} />
        <Route path="/business" element={<BusinessBuilderPage />} />
        <Route path="/learning" element={<LearningHubPage />} />
        <Route path="/opportunities" element={<OpportunityRadarPage />} />
        <Route path="/future-preview" element={<FuturePreviewPage />} />
        <Route path="/video-call" element={<VideoCallPage />} />
        <Route path="/video-call/:roomId" element={<VideoCallPage />} />
      </Route>

      {/* ===== PROTECTED ROUTES (Login Required - Personal/Admin) ===== */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Route>

      {/* ===== PUBLIC STANDALONE FEATURE ROUTES (No Login Required) ===== */}
      {/* Aira Echo - Full Screen Social Media */}
      <Route path="/aira-echo" element={<AiraEchoPage />} />
      
      {/* Echo routes (new redesigned version) */}
      <Route path="/echo/*" element={<AiraEchoPage />} />

      {/* Future Echo - Time Capsules */}
      <Route path="/future-echo" element={<FutureEchoPage />} />

      {/* Echo Souls - AI-Matched Connections */}
      <Route path="/echo-souls" element={<EchoSoulsPage />} />

      {/* AIRA Experiences - Premium Event Discovery */}
      <Route path="/experiences" element={<ExperiencesPage />} />

      {/* AI Trip Planner - Premium AI-powered itinerary generation */}
      <Route path="/trip-planner" element={<TripPlannerPage />} />

      {/* AIRA Film Studio - Create Your Own Film */}
      <Route path="/film-studio" element={<FilmStudioPage />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Theme-aware Toaster component
function ThemedToaster() {
  const { isDark } = useTheme();
  
  const toasterTheme = isDark ? 'dark' : 'light';
  const toasterStyles = isDark 
    ? {
        background: 'rgba(10, 10, 10, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: '#fff',
        backdropFilter: 'blur(16px)'
      }
    : {
        background: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        color: '#1a1a2e',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
      };

  return (
    <Toaster 
      position="top-right" 
      theme={toasterTheme}
      toastOptions={{
        style: toasterStyles
      }}
    />
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <VoiceProvider>
          <AuthProvider>
            <AppRouter />
            <GlobalWakeWordListener />
            <ThemedToaster />
            <CookieConsentBanner />
            <GlobalVoiceAssistant />
          </AuthProvider>
        </VoiceProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
